import { jsPDF } from "jspdf";

const MAX_SIDE = 2000;

export const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

const rotatedCanvas = (img, rotation, maxSide) => {
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);
  const swap = rotation % 180 !== 0;
  const c = document.createElement("canvas");
  c.width = swap ? h : w;
  c.height = swap ? w : h;
  const ctx = c.getContext("2d");
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  return c;
};

// Pixel-level adjust: canvas ctx.filter is unsupported on iOS Safari
const adjustPixels = (ctx, w, h, brightness, contrast) => {
  if (brightness === 100 && contrast === 100) return;
  const data = ctx.getImageData(0, 0, w, h);
  const b = brightness / 100;
  const k = contrast / 100;
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      px[i + j] = Math.max(0, Math.min(255, ((px[i + j] * b - 128) * k) + 128));
    }
  }
  ctx.putImageData(data, 0, 0);
};

export const rotatedPreview = async (src, rotation) => {
  const img = await loadImage(src);
  return rotatedCanvas(img, rotation, 1200).toDataURL("image/jpeg", 0.8);
};

export const renderPage = async (src, { rotation, crop, brightness, contrast }) => {
  const img = await loadImage(src);
  const r = rotatedCanvas(img, rotation, MAX_SIDE);
  const c = crop && crop.width > 0 && crop.height > 0 ? crop : { x: 0, y: 0, width: 100, height: 100 };
  const sx = Math.round((c.x / 100) * r.width);
  const sy = Math.round((c.y / 100) * r.height);
  const sw = Math.max(1, Math.round((c.width / 100) * r.width));
  const sh = Math.max(1, Math.round((c.height / 100) * r.height));
  const out = document.createElement("canvas");
  out.width = sw;
  out.height = sh;
  const ctx = out.getContext("2d");
  ctx.drawImage(r, sx, sy, sw, sh, 0, 0, sw, sh);
  adjustPixels(ctx, sw, sh, brightness, contrast);
  return { dataUrl: out.toDataURL("image/jpeg", 0.85), width: sw, height: sh };
};

const dataUrlToFile = async (dataUrl, name, type) => {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], name, { type });
};

export const buildDocumentFile = async (pages, baseName) => {
  if (pages.length === 1) return dataUrlToFile(pages[0].dataUrl, `${baseName}.jpg`, "image/jpeg");
  const first = pages[0];
  const pdf = new jsPDF({ unit: "px", format: [first.width, first.height], orientation: first.width > first.height ? "l" : "p", hotfixes: ["px_scaling"] });
  pages.forEach((p, i) => {
    if (i > 0) pdf.addPage([p.width, p.height], p.width > p.height ? "l" : "p");
    pdf.addImage(p.dataUrl, "JPEG", 0, 0, p.width, p.height);
  });
  return new File([pdf.output("blob")], `${baseName}.pdf`, { type: "application/pdf" });
};
